import React, { useState, useEffect, useCallback } from 'react'
import { HART_BASE_URL, HART_CATEGORIES } from '../lib/defaults'
import { supabase } from '../lib/supabase'

export default function HartCatalogue({ onAddItems, onClose, userId, initialBasket }) {
  const [selectedCategory, setSelectedCategory] = useState(HART_CATEGORIES[0].subcategories[0])
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [basket, setBasket] = useState(initialBasket || [])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchMode, setSearchMode] = useState(false)
  const [favouriteIds, setFavouriteIds] = useState([])
  const [favouriteProducts, setFavouriteProducts] = useState([])
  const [showFavourites, setShowFavourites] = useState(false)

  useEffect(() => { loadFavourites() }, [])
  useEffect(() => {
    if (!showFavourites && !searchMode) fetchProducts(selectedCategory.collection)
  }, [selectedCategory, showFavourites, searchMode])

  const loadFavourites = async () => {
    const { data } = await supabase.from('hart_favourites').select('*').order('created_at', { ascending: false })
    if (!data) return
    setFavouriteIds(data.map(f => String(f.product_id)))
    setFavouriteProducts(data)
    // Backfill missing images from Hart API
    const missing = data.filter(f => !f.product_image && f.product_handle)
    for (const fav of missing) {
      fetchFullProduct(fav.product_handle).then(product => {
        const imgSrc = (typeof product?.images?.[0] === 'string' ? product.images[0] : product?.images?.[0]?.src) || null
        if (imgSrc) {
          supabase.from('hart_favourites').update({ product_image: imgSrc }).eq('id', fav.id)
          setFavouriteProducts(prev => prev.map(f => f.id === fav.id ? { ...f, product_image: imgSrc } : f))
        }
      })
    }
  }

  const toggleFavourite = async (product) => {
    const productId = String(product.id)
    const isFavItem = favouriteIds.includes(productId)
    if (isFavItem) {
      await supabase.from('hart_favourites').delete().eq('product_id', productId)
      setFavouriteIds(prev => prev.filter(id => id !== productId))
      setFavouriteProducts(prev => prev.filter(f => String(f.product_id) !== productId))
    } else {
      const record = {
        user_id: userId, product_id: productId,
        product_title: product.title,
        product_sku: product.variants?.[0]?.sku || '',
        product_price: parseFloat(product.variants?.[0]?.price || 0),
        product_handle: product.handle,
        product_image: (typeof product.images?.[0] === 'string' ? product.images[0] : product.images?.[0]?.src) || product.featured_image?.url || null,
      }
      const { data } = await supabase.from('hart_favourites').insert(record).select().single()
      setFavouriteIds(prev => [...prev, productId])
      if (data) setFavouriteProducts(prev => [data, ...prev])
    }
  }

  const fetchProducts = useCallback(async (collection, query = '') => {
    setLoading(true)
    setSelectedProduct(null)
    try {
      if (query) {
        // Use Shopify's predictive search API — richer results than suggest.json
        const url = `/api/hart?path=search/suggest.json&q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=24&resources[fields]=title,product_type,variants.sku,variants.title`
        const res = await fetch(url)
        const data = await res.json()
        const raw = data.resources?.results?.products || []
        // Normalise suggest.json shape to match products.json shape
        const normalised = raw.map(p => ({
          id: String(p.id),
          title: p.title,
          handle: p.handle,
          // featured_image.url is the correct field from suggest.json
          images: p.featured_image?.url ? [p.featured_image.url] : [],
          // variants is always [] in suggest.json — price is at root as string pounds
          // We mark _needsVariantFetch so addToBasket fetches the full product for variant ID
          variants: [{
            price: String(p.price || 0),  // root price is string pounds e.g. "2.81"
            sku: p.variants?.[0]?.sku || '',
            id: '',  // unknown until full fetch
          }],
          body_html: p.body || '',
          _needsVariantFetch: true,
        }))
        setProducts(normalised)
      } else {
        const url = `/api/hart?path=collections/${collection}/products.json&limit=50`
        const res = await fetch(url)
        const data = await res.json()
        setProducts(data.products || [])
      }
    } catch { setProducts([]) }
    setLoading(false)
  }, [])

  // When a search result product is selected, fetch full product data to get real variant IDs
  const fetchFullProduct = async (handle) => {
    try {
      const res = await fetch(`/api/hart?path=products/${handle}.json`)
      const data = await res.json()
      return data.product || null
    } catch { return null }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    setSearchMode(true); setShowFavourites(false)
    fetchProducts(null, searchQuery)
  }

  const clearSearch = () => {
    setSearchMode(false); setSearchQuery(''); setShowFavourites(false)
    fetchProducts(selectedCategory.collection)
  }

  const selectProduct = async (product) => {
    if (selectedProduct && String(selectedProduct.id) === String(product.id)) {
      setSelectedProduct(null); return
    }
    // If from search results, fetch full product data for variant IDs + description
    let fullProduct = product
    if (product._needsVariantFetch) {
      const fetched = await fetchFullProduct(product.handle)
      if (fetched) fullProduct = fetched
    }
    setSelectedProduct(fullProduct)
  }

  // Normalise price — recommendations API returns pence as integer, collections returns string pounds
  const getPriceInPounds = (product) => {
    const raw = product.variants?.[0]?.price ?? product.price ?? 0
    const num = parseFloat(raw)
    // If price looks like pence (integer > 20 and no decimal point in raw string)
    // Use heuristic: if raw is a number type (not string), it's pence
    if (typeof raw === 'number' && Number.isInteger(raw)) return num / 100
    return num
  }

  const addToBasket = async (product) => {
    let p = product
    // If search result without real variant ID, fetch first
    if (product._needsVariantFetch) {
      const fetched = await fetchFullProduct(product.handle)
      if (fetched) p = fetched
    }
    const price = getPriceInPounds(p)
    const id = String(p.id)
    const variantId = String(p.variants?.[0]?.id || '')
    const title = p.title
    const sku = p.variants?.[0]?.sku || ''
    setBasket(prev => {
      const existing = prev.find(b => b.id === id)
      if (existing) return prev.map(b => b.id === id ? { ...b, qty: b.qty + 1 } : b)
      return [...prev, { id, variantId, sku, name: title, price, qty: 1, procurement: 'order' }]
    })
  }

  const checkoutOnHart = () => {
    const orderItems = basket.filter(b => b.procurement === 'order' && b.variantId)
    if (!orderItems.length) return
    const cartPath = orderItems.map(b => `${b.variantId}:${b.qty}`).join(',')
    window.open(`${HART_BASE_URL}/cart/${cartPath}?storefront=true`, '_blank')
  }

  const removeFromBasket = (id) => setBasket(prev => prev.filter(b => b.id !== id))
  const toggleProc = (id) => setBasket(prev => prev.map(b => b.id === id ? { ...b, procurement: b.procurement === 'order' ? 'stock' : 'order' } : b))
  const updateQty = (id, delta) => setBasket(prev => prev.map(b => b.id === id ? { ...b, qty: Math.max(1, b.qty + delta) } : b))
  const isInBasket = (id) => basket.some(b => b.id === String(id))
  const isFav = (id) => favouriteIds.includes(String(id))

  const basketSubtotal = basket.reduce((sum, b) => sum + b.price * b.qty, 0)
  const basketVat = basketSubtotal * 0.2

  const handleAddToProject = () => {
    if (!basket.length) return
    onAddItems(basket, basketSubtotal + basketVat)
    onClose()
  }

  const getImg = (product, size = 80) => {
    // recommendations API returns images as string array; collections API returns objects with .src
    const raw = product.images?.[0]
    const src = (typeof raw === 'string' ? raw : raw?.src) || product.featured_image?.url || product.product_image
    if (!src) return null
    // Ensure protocol prefix
    const full = src.startsWith('//') ? 'https:' + src : src
    return full.replace(/(\.[^.]+)$/, `_${size}x${size}$1`)
  }

  const normaliseFav = (f) => ({
    id: f.product_id, title: f.product_title,
    handle: f.product_handle,
    // variant ID is not stored in hart_favourites — fetch full product to get it
    variants: [{ sku: f.product_sku, price: String(f.product_price), id: '' }],
    images: f.product_image ? [f.product_image] : [],
    _needsVariantFetch: true,
  })

  const displayProducts = showFavourites ? favouriteProducts.map(normaliseFav) : products

  // Shared product row renderer
  const ProductRow = ({ product, compact = false }) => {
    const selected = selectedProduct && String(selectedProduct.id) === String(product.id)
    const price = getPriceInPounds(product)
    const inBasket = isInBasket(product.id)
    const thumbSize = compact ? 38 : 48
    return (
      <div
        className={`ak-row${selected ? ' is-selected' : ''}`}
        style={{
          '--cols': `${thumbSize + 8}px 1fr auto`,
          cursor: 'pointer',
          height: compact ? '50px' : '60px',
          padding: compact ? '0 0.75rem' : '0 0.75rem',
          gap: '0.6rem',
          alignItems: 'center',
        }}
        onClick={() => selectProduct(product)}
      >
        {getImg(product, 56)
          ? <img
              src={getImg(product, 56)}
              alt=""
              style={{ width: thumbSize, height: thumbSize, borderRadius: 'var(--radius-ctl)', border: '1px solid var(--line)', objectFit: 'cover', flexShrink: 0 }}
              onError={e => e.target.style.display = 'none'}
            />
          : <div style={{ width: thumbSize, height: thumbSize, borderRadius: 'var(--radius-ctl)', border: '1px solid var(--line)', background: 'var(--surface-2)', flexShrink: 0 }} />
        }
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="ak-cell-name" style={{ fontSize: '0.78rem' }}>{product.title}</div>
          <div className="ak-cell-sub">{product.variants?.[0]?.sku}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem', flexShrink: 0 }}>
          <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--c-fixings)', fontWeight: 500 }}>
            £{price.toFixed(2)}
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {!compact && (
              <button
                className="ak-iconbtn"
                style={{ color: isFav(product.id) ? 'var(--c-adhesives)' : 'var(--ink-3)', width: '22px', height: '22px', fontSize: '0.95rem' }}
                onClick={e => { e.stopPropagation(); toggleFavourite(product) }}
              >★</button>
            )}
            <button
              className="ak-iconbtn"
              style={{
                width: '22px', height: '22px', borderRadius: 'var(--radius-ctl)',
                border: `1.5px solid ${inBasket ? 'var(--green)' : 'var(--c-fixings)'}`,
                background: inBasket ? 'var(--green)' : 'transparent',
                color: inBasket ? '#fff' : 'var(--c-fixings)',
                fontSize: '0.9rem',
              }}
              onClick={e => { e.stopPropagation(); addToBasket(product) }}
            >
              {inBasket ? '✓' : '+'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '1.25rem' }}>

      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <span className="t-label" style={{ color: 'var(--c-fixings)' }}>Hart Wholesale — Blum Catalogue</span>
        <span
          className="ak-badge"
          style={{ color: 'var(--c-fixings)', background: 'color-mix(in srgb,var(--c-fixings) 10%,var(--surface))', border: '1px solid color-mix(in srgb,var(--c-fixings) 25%,var(--line))' }}
        >Live prices ex-VAT</span>
        <button className="ak-btn ak-btn--ghost ak-btn--sm" style={{ marginLeft: 'auto' }} onClick={onClose}>Close ×</button>
      </div>

      {/* Outer grid: sidebar | main | basket */}
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 230px', gap: '0.75rem', alignItems: 'start' }}>

        {/* Sidebar */}
        <div className="ak-card" style={{ maxHeight: '640px', overflowY: 'auto' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--ink-3)', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
            Browse
          </div>

          {/* Favourites item */}
          <div
            style={{
              padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer',
              borderBottom: '1px solid var(--line)', color: 'var(--ink)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontWeight: 600,
              ...(showFavourites ? { background: 'color-mix(in srgb,var(--c-fixings) 10%,var(--surface))', color: 'var(--c-fixings)' } : {}),
            }}
            onClick={() => { setShowFavourites(true); setSearchMode(false); setSearchQuery('') }}
          >
            <span>★ Favourites</span>
            {favouriteIds.length > 0 && (
              <span className="ak-badge">{favouriteIds.length}</span>
            )}
          </div>

          <div style={{ borderTop: '2px solid var(--line)' }} />

          {HART_CATEGORIES.map(group => (
            <div key={group.name}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--ink-3)', padding: '0.35rem 0.75rem 0.2rem', background: 'var(--surface-2)', position: 'sticky', top: '32px' }}>
                {group.name}
              </div>
              {group.subcategories.map(sub => (
                <div
                  key={sub.collection}
                  style={{
                    padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer',
                    borderBottom: '1px solid rgba(0,0,0,0.04)', color: 'var(--ink)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    ...(selectedCategory.collection === sub.collection && !showFavourites && !searchMode
                      ? { background: 'color-mix(in srgb,var(--c-fixings) 10%,var(--surface))', color: 'var(--c-fixings)', fontWeight: 600 }
                      : {}),
                  }}
                  onClick={() => { setSelectedCategory(sub); setShowFavourites(false); setSearchMode(false); setSearchQuery(''); setSelectedProduct(null) }}
                  title={sub.description}
                >{sub.name}</div>
              ))}
            </div>
          ))}
        </div>

        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>

          {/* Search bar */}
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <div className="ak-search" style={{ flex: 1 }}>
              <input
                placeholder="Search all Hart products by name or SKU…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <button type="submit" className="ak-btn ak-btn--primary ak-btn--sm">Search</button>
            {(searchMode || showFavourites) && (
              <button type="button" className="ak-btn ak-btn--ghost ak-btn--sm" onClick={clearSearch}>Clear</button>
            )}
          </form>

          {/* Product list + optional detail panel */}
          <div style={{ position: 'relative' }}>
            <div className="ak-card" style={{ overflow: 'hidden' }}>
              {/* Section label */}
              <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--ink-3)', padding: '0.35rem 0.75rem', borderBottom: '1px solid var(--line)', textTransform: 'uppercase', letterSpacing: '0.8px', background: 'var(--surface-2)' }}>
                {showFavourites
                  ? `★ Favourites (${favouriteProducts.length})`
                  : searchMode
                  ? `Results for "${searchQuery}"`
                  : `${selectedCategory.name}${selectedCategory.description ? ' — ' + selectedCategory.description : ''}`}
              </div>
              <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                {loading && (
                  <div style={{ padding: '1.5rem', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--ink-3)' }}>Loading…</div>
                )}
                {!loading && displayProducts.length === 0 && (
                  <div style={{ padding: '1.5rem', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--ink-3)' }}>
                    {showFavourites ? 'No favourites yet — click ★ on any product' : 'No products found'}
                  </div>
                )}
                {!loading && displayProducts.map(product => (
                  <ProductRow key={product.id} product={product} />
                ))}
              </div>
            </div>

            {/* Detail panel — floats over the product list when a product is selected */}
            {selectedProduct && (
              <div style={{ position: 'absolute', top: 0, right: 0, width: '320px', maxHeight: '480px', overflowY: 'auto', background: 'var(--surface)', border: `1.5px solid var(--c-fixings)`, borderRadius: 'var(--radius)', zIndex: 10, boxShadow: 'var(--shadow-lg)' }}>
                <button
                  className="ak-btn ak-btn--ghost ak-btn--sm"
                  style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 1 }}
                  onClick={() => setSelectedProduct(null)}
                >× Close</button>

                <div style={{ padding: '0.75rem' }}>
                  {getImg(selectedProduct, 400) && (
                    <img
                      src={getImg(selectedProduct, 400)}
                      alt=""
                      style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 'var(--radius)', border: '1px solid var(--line)', background: 'var(--surface-2)', marginBottom: '0.6rem' }}
                      onError={e => e.target.style.display = 'none'}
                    />
                  )}
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.4, marginBottom: '0.25rem', color: 'var(--ink)' }}>{selectedProduct.title}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--ink-3)', marginBottom: '0.2rem' }}>
                    {selectedProduct.variants?.[0]?.sku}
                  </div>
                  <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: '1rem', color: 'var(--c-fixings)', fontWeight: 600, marginBottom: '0.4rem' }}>
                    £{parseFloat(selectedProduct.variants?.[0]?.price || 0).toFixed(2)}
                    <span style={{ fontWeight: 400, fontSize: '0.62rem', color: 'var(--ink-3)' }}> ex-VAT</span>
                  </div>
                  {selectedProduct.body_html && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: '0.4rem' }}>
                      {selectedProduct.body_html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300)}
                    </div>
                  )}
                  {selectedProduct.images?.length > 1 && (
                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      {selectedProduct.images.slice(1, 5).map((img, i) => (
                        <img
                          key={i}
                          src={img.src.replace(/(\.[^.]+)$/, '_70x70$1')}
                          alt=""
                          style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: 'var(--radius-ctl)', border: '1px solid var(--line)' }}
                          onError={e => e.target.style.display = 'none'}
                        />
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.75rem' }}>
                    <button
                      className="ak-btn ak-btn--primary"
                      style={{ flex: 1, justifyContent: 'center' }}
                      onClick={() => addToBasket(selectedProduct)}
                    >
                      {isInBasket(selectedProduct.id) ? '✓ In basket' : '+ Add to basket'}
                    </button>
                    <a
                      href={`${HART_BASE_URL}/products/${selectedProduct.handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ak-btn ak-btn--ghost"
                      style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', whiteSpace: 'nowrap', textDecoration: 'none' }}
                    >
                      View on Hart ↗
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Basket */}
        <div className="ak-card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'var(--mono)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--ink-3)', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--line)' }}>
            <span>Basket</span>
            <span className="tnum" style={{ marginLeft: 'auto', color: 'var(--c-fixings)', fontWeight: 500 }}>{basket.length} items</span>
          </div>

          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {basket.length === 0 && (
              <div style={{ padding: '1.25rem', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--ink-3)' }}>
                Add items from the list
              </div>
            )}
            {basket.map(item => (
              <div key={item.id} style={{ padding: '0.4rem 0.75rem', borderBottom: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, fontSize: '0.75rem', lineHeight: 1.3, color: 'var(--ink)' }}>{item.name}</div>
                  <div className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', fontWeight: 500, flexShrink: 0 }}>
                    £{(item.price * item.qty).toFixed(2)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '3px' }}>
                  <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--ink-3)' }}>
                    {item.sku} · {item.qty}×£{item.price.toFixed(2)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <button
                      className="ak-iconbtn"
                      style={{ width: '16px', height: '16px', border: '1px solid var(--line-2)', background: 'var(--surface-3)', fontSize: '0.8rem' }}
                      onClick={() => updateQty(item.id, -1)}
                    >−</button>
                    <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', minWidth: '14px', textAlign: 'center' }}>{item.qty}</span>
                    <button
                      className="ak-iconbtn"
                      style={{ width: '16px', height: '16px', border: '1px solid var(--line-2)', background: 'var(--surface-3)', fontSize: '0.8rem' }}
                      onClick={() => updateQty(item.id, 1)}
                    >+</button>
                    <button
                      className={`proc-pill proc-${item.procurement}`}
                      style={{ border: 'none', cursor: 'pointer' }}
                      onClick={() => toggleProc(item.id)}
                    >
                      {item.procurement === 'order' ? 'To order' : 'In stock'}
                    </button>
                    <button
                      className="ak-iconbtn ak-iconbtn--danger"
                      style={{ width: '18px', height: '18px', fontSize: '0.9rem' }}
                      onClick={() => removeFromBasket(item.id)}
                    >×</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {basket.length > 0 && (
            <div className="sum-group" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--line)' }}>
              <div className="sum-row">
                <span className="k">Subtotal</span>
                <span className="v tnum">£{basketSubtotal.toFixed(2)}</span>
              </div>
              <div className="sum-row muted">
                <span className="k">VAT 20%</span>
                <span className="v tnum">£{basketVat.toFixed(2)}</span>
              </div>
              <div className="sum-row sub">
                <span className="k">Total inc. VAT</span>
                <span className="v tnum">£{(basketSubtotal + basketVat).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.35rem', marginTop: '0.5rem', paddingTop: '0.4rem', borderTop: '1px dashed var(--line)', fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--ink-3)', lineHeight: 1.4 }}>
                <span style={{ flexShrink: 0 }}>🚚</span>
                <span>
                  {basketSubtotal >= 100 ? 'Free standard delivery (order over £100)' : 'Standard delivery £7 (free over £100)'}
                  {' — '}
                  <a href="https://www.blum.org.uk/pages/delivery-and-returns" target="_blank" rel="noreferrer" style={{ color: 'var(--c-fixings)', textDecoration: 'underline' }}>
                    delivery info
                  </a>
                </span>
              </div>
            </div>
          )}

          {basket.some(b => b.procurement === 'order') && (
            <button
              className="ak-btn ak-btn--primary ak-btn--block"
              style={{ borderRadius: 0, padding: '0.6rem' }}
              onClick={checkoutOnHart}
            >
              🛒 Checkout on Hart →
            </button>
          )}

          <button
            className="ak-btn ak-btn--dark ak-btn--block"
            style={{ borderRadius: 0, padding: '0.65rem', opacity: basket.length ? 1 : 0.5 }}
            onClick={handleAddToProject}
            disabled={!basket.length}
          >
            Add to Fixings →
          </button>
        </div>

      </div>
    </div>
  )
}
